import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ICampaign } from '../../../../models/campaign';
import { ISession } from '../../../../models/session';
import { ISegment, SegmentsService } from '../../../sessions/segments.service';
import { TranslateService } from '@ngx-translate/core';
import { isDateValid } from '../../../../utils/html.utils';
import { UsersService } from '../../../sessions/users/users.service';
import { IRegistrationField } from '../../../../models/registrationField';

@Component({
  selector: 'edit-campaign',
  templateUrl: './edit-campaign.component.html',
  styleUrls: ['./edit-campaign.component.scss']
})
export class EditCampaignComponent implements OnChanges, OnInit {
  @Input() campaign: ICampaign;
  @Input() session: ISession;
  @Input() recipients: {email: string, firstName: string, lastName: string}[] = [];
  @Output() campaignChange = new EventEmitter<ICampaign>();

  public draft: ICampaign;
  public recipientSearch = '';
  public recipientType: 'mail_list' | 'segments' | 'all' = 'mail_list';
  public segments: ISegment[] = [];
  public isLoadingSegments = false;
  public isDateValid = isDateValid;
  public registrationFields: IRegistrationField[] = [];
  public isDateInPast = (date: Date | string | undefined): boolean => {
    if (!isDateValid(date)) return false;
    return new Date(date || '') < new Date();
  };

  // Add segment modal
  public showAddSegmentModal = false;
  public newSegment: ISegment | null = null;
  public isSavingSegment = false;

  constructor(
    private segmentsService: SegmentsService,
    private translateService: TranslateService,
    private usersService: UsersService,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.campaign && this.campaign) {
      this.draft = {
        ...this.campaign,
        mailing_list: this.campaign.mailing_list ? [...this.campaign.mailing_list] : [],
      };
    }
  }

  ngOnInit() {
    if (this.campaign.recipient_mode === 'all') {
      this.recipientType = 'all';
    } else if (this.campaign.recipient_mode === 'segment' || this.campaign.segment_id > 0) {
      this.recipientType = 'segments';
    } else {
      this.recipientType = 'mail_list';
    }
    this.loadSegments();
    this.loadRegistrationForm();
  }

  public loadRegistrationForm() {
    this.usersService.getRegistration(this.session).subscribe(
      (fields: any[]) => { this.registrationFields = fields || []; },
      _err => toastr.error(this.translateService.instant('action.failed'))
    );
  }

  public loadSegments() {
    this.isLoadingSegments = true;
    this.segmentsService.getSegments(this.session).subscribe(
      (segments: ISegment[]) => {
        this.segments = segments || [];
        this.isLoadingSegments = false;
      },
      _err => {
        toastr.error(this.translateService.instant('action.failed'));
        this.isLoadingSegments = false;
      }
    );
  }

  get isScheduled(): boolean {
    return this.draft && this.draft.mode === 'scheduled';
  }

  public toggleEmail(email: string) {
    const list = this.draft.mailing_list || [];
    const idx = list.indexOf(email);
    if (idx === -1) {
      this.draft.mailing_list = [...list, email];
    } else {
      this.draft.mailing_list = list.filter(e => e !== email);
    }
  }

  public isSelected(email: string): boolean {
    return (this.draft.mailing_list || []).indexOf(email) !== -1;
  }

  get filteredRecipients(): {email: string, firstName: string, lastName: string}[] {
    const normalize = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    const q = normalize(this.recipientSearch.trim());
    if (!q) return this.recipients;
    return this.recipients.filter(r =>
      normalize(r.email || '').indexOf(q) !== -1 ||
      normalize(r.firstName || '').indexOf(q) !== -1 ||
      normalize(r.lastName || '').indexOf(q) !== -1
    );
  }

  public allSelected(): boolean {
    const emails = this.filteredRecipients.map((r: {email: string}) => r.email);
    return emails.length > 0 && emails.every((e: string) => this.isSelected(e));
  }

  public someSelected(): boolean {
    const emails = this.filteredRecipients.map((r: {email: string}) => r.email);
    return emails.some((e: string) => this.isSelected(e)) && !this.allSelected();
  }

  public toggleAll(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    const visibleEmails = this.filteredRecipients.map((r: {email: string}) => r.email);
    if (checked) {
      const current = this.draft.mailing_list || [];
      const merged = current.concat(visibleEmails.filter((e: string) => current.indexOf(e) === -1));
      this.draft.mailing_list = merged;
    } else {
      this.draft.mailing_list = (this.draft.mailing_list || []).filter((e: string) => visibleEmails.indexOf(e) === -1);
    }
    this.emit();
  }

  public onModeChange() {
    if (this.draft.mode !== 'scheduled') {
      this.draft.scheduled_at = null;
    }
  }

  public openAddSegmentModal() {
    this.newSegment = { name: '', search_criteria: { filters: [] } };
    this.showAddSegmentModal = true;
  }

  public closeAddSegmentModal() {
    this.showAddSegmentModal = false;
    this.newSegment = null;
  }

  public onNewSegmentChange(segment: ISegment) {
    this.newSegment = segment;
  }

  public saveNewSegment() {
    if (!this.newSegment || !this.newSegment.name) return;
    this.isSavingSegment = true;
    const dto: ISegment = {
      name: this.newSegment.name,
      search_criteria: { filters: this.newSegment.search_criteria ? this.newSegment.search_criteria.filters : [] }
    };
    this.segmentsService.createSegment(this.session, dto).subscribe(
      (created: ISegment) => {
        this.isSavingSegment = false;
        this.showAddSegmentModal = false;
        this.loadSegments();
        this.draft.segment_id = created.segment_id ? +created.segment_id : undefined;
        this.emit();
      },
      _err => {
        toastr.error(this.translateService.instant('action.failed'));
        this.isSavingSegment = false;
      }
    );
  }

  public emit() {
    this.campaignChange.emit({
      ...this.draft,
      ...(this.recipientType === 'segments' && { mailing_list: [], recipient_mode: 'segment' }),
      ...(this.recipientType === 'mail_list' && { segment_id: 0, recipient_mode: 'email_list' }),
      ...(this.recipientType === 'all' && { mailing_list: [], segment_id: 0, recipient_mode: 'all' }),
      ...(this.draft.mode !== 'scheduled' && { scheduled_at: null })
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/email/campaigns/edit-campaign/edit-campaign.component.ts